import React from 'react';

import { Links } from '../../constants/links';

import { MenuItem } from '../menu/MenuItem.view';
import { UserBanner } from '../user-banner';
import { MenuAction } from '../menu/MenuAction.view';
import { MenuActionLink } from './MenuActionLink.view';
import { ExternalLink } from 'Common/Links/ExternalLink';
import { MenuToggleButton } from './MenuToggleButton.view';
import { MenuSeparator } from '../menu/MenuSeparator.view';
import { MenuSectionHeader } from './MenuSectionHeader.view';

import { TestID } from '../../testID';

import type { AppThunk } from '../../types/state.types';

import './UserMenu.css';

export const UserMenu: React.FC<{
  canTogglePortalAdminPowers: boolean;
  isPortalAdmin: boolean;
  isLiveUpdatesEnabled?: boolean;
  darkModeActive: boolean;
  email: string;
  userName: string;
  logout: () => AppThunk;
  setPortalAdminPowerActivationState: (activate: boolean) => void;
  toggleEntitiesLiveUpdates: (isChecked: boolean) => void;
  toggleTheme: (isChecked: boolean) => void;
}> = ({
  canTogglePortalAdminPowers,
  isPortalAdmin,
  isLiveUpdatesEnabled,
  darkModeActive,
  email,
  userName,
  logout,
  setPortalAdminPowerActivationState,
  toggleEntitiesLiveUpdates,
  toggleTheme,
}) => (
  <>
    <MenuItem>
      <UserBanner className="user-menu__user-banner" name={userName} email={email} />
    </MenuItem>
    <MenuSeparator />
    <MenuSectionHeader title="Account" />
    <MenuActionLink to="/user/profile" label="Profile" />
    <MenuActionLink to="/user/payment-methods" label="Payment methods" />
    {canTogglePortalAdminPowers && (
      <MenuToggleButton
        text="Portal-admin powers"
        isDangerText={true}
        title="Toggle portal-admin powers"
        isOn={isPortalAdmin}
        onToggle={setPortalAdminPowerActivationState}
        testId={TestID.AdminPowersToggleButton}
      />
    )}
    {isPortalAdmin && (
      <ExternalLink
        href="/api/admin"
        className="menu__action"
        testId={TestID.AdminGuiExternalLink}
        showOpenInIcon={true}
        title="Open Admin GUI link in new tab"
      >
        Admin GUI
      </ExternalLink>
    )}
    <MenuSeparator />
    <MenuSectionHeader title="Global settings" />
    <MenuToggleButton
      text="Dark mode"
      title="Toggle theme"
      isOn={darkModeActive}
      onToggle={toggleTheme}
      testId={TestID.ToggleThemeButton}
    />
    <MenuToggleButton
      text="Live updates"
      title="Toggle to enable or disable live updates. When enabled, the server automatically sends real-time updates for Systems, Pipes, Datasets, and Entities"
      isOn={isLiveUpdatesEnabled}
      onToggle={toggleEntitiesLiveUpdates}
      wrapperStyle={{ marginTop: '10px' }}
      testId={TestID.ToggleLiveUpdatesButton}
    />
    <MenuSeparator />
    <MenuSectionHeader title="Help & Documentation" />
    <ExternalLink
      href={Links.SesamSupport}
      className="menu__action"
      testId={TestID.SesamSupportLink}
      showOpenInIcon={true}
      title="Open help center link in new tab"
    >
      Help Center
    </ExternalLink>
    <ExternalLink
      href={Links.SesamPrices}
      className="menu__action"
      testId={TestID.SesamPricingLink}
      showOpenInIcon={true}
      title="Open prices link in new tab"
    >
      Prices
    </ExternalLink>
    <MenuSeparator />
    <MenuAction label="Logout" onClick={logout} />
  </>
);
